import React, { useState, useMemo } from 'react';
import { Row, Col, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { SalesPerformanceProps } from '../types';

const SalesPerformance: React.FC<SalesPerformanceProps> = ({
  storeRevenue,
  revenueLoading,
  fulfilledOrders: totalOrders,
  averageOrderValue,
  summaryLoading,
  dailyRevenue = [],
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Layout constants for SVG
  const svgWidth = 1000;
  const svgHeight = 175;
  const padX = 24;
  const padTop = 20;
  const padBottom = 35;
  const chartWidth = svgWidth - padX * 2;
  const chartHeight = svgHeight - padTop - padBottom;

  const points = useMemo(() => {
    if (!dailyRevenue || dailyRevenue.length === 0) return [];

    const amounts = dailyRevenue.map(
      (d) => parseFloat(String(d.total_amount)) || 0
    );
    const maxAmount = Math.max(...amounts, 1);
    const yScaleMax = maxAmount * 1.25;
    const n = dailyRevenue.length;

    return dailyRevenue.map((d, i) => {
      const amount = parseFloat(String(d.total_amount)) || 0;
      const x =
        n > 1 ? padX + (i / (n - 1)) * chartWidth : padX + chartWidth / 2;
      const y = padTop + chartHeight - (amount / yScaleMax) * chartHeight;
      const isToday = moment(d.date).isSame(moment(), 'day') || i === n - 1;

      return {
        ...d,
        amount,
        x,
        y,
        isToday,
        dayLabel: moment(d.date).format('ddd'),
      };
    });
  }, [dailyRevenue, chartWidth, chartHeight, padX, padTop]);

  // Generate smooth cubic bezier curve
  const { pathD, areaD } = useMemo(() => {
    if (points.length === 0) return { pathD: '', areaD: '' };
    if (points.length === 1) {
      const p = points[0];
      return {
        pathD: `M ${p.x - 20} ${p.y} L ${p.x + 20} ${p.y}`,
        areaD: `M ${p.x - 20} ${p.y} L ${p.x + 20} ${p.y} L ${p.x + 20} ${padTop + chartHeight} L ${p.x - 20} ${padTop + chartHeight} Z`,
      };
    }

    let d = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i];
      const p1 = points[i + 1];
      const dx = (p1.x - p0.x) * 0.45;
      d += ` C ${p0.x + dx} ${p0.y}, ${p1.x - dx} ${p1.y}, ${p1.x} ${p1.y}`;
    }

    const baselineY = padTop + chartHeight;
    const area = `${d} L ${points[points.length - 1].x} ${baselineY} L ${points[0].x} ${baselineY} Z`;

    return { pathD: d, areaD: area };
  }, [points, padTop, chartHeight]);

  const activeHoveredPoint =
    hoveredIndex !== null && points[hoveredIndex] ? points[hoveredIndex] : null;

  return (
    <div className="bg-white rounded-4 border border-1 border-light-subtle p-4 shadow-sm">
      <div className="mb-4">
        <h4 className="fw-bold text-dark mb-0 fs-5">Sales performance</h4>
      </div>

      {/* Metric Cards Row */}
      <Row className="g-3 mb-4">
        <Col md={4}>
          <div
            className="p-4 rounded-4 h-100"
            style={{ backgroundColor: '#EFF6FF' }}
          >
            <div
              className="text-secondary text-uppercase fw-semibold mb-2"
              style={{ fontSize: '11px', letterSpacing: '0.5px' }}
            >
              REVENUE
            </div>
            <div className="fs-2 fw-bold text-dark">
              {revenueLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                `Rs. ${Number(storeRevenue || 0).toLocaleString()}`
              )}
            </div>
          </div>
        </Col>

        <Col md={4}>
          <div
            className="p-4 rounded-4 h-100"
            style={{ backgroundColor: '#EFF6FF' }}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div
                className="text-secondary text-uppercase fw-semibold"
                style={{ fontSize: '11px', letterSpacing: '0.5px' }}
              >
                FULFILLED ORDERS
              </div>
            </div>
            <div className="fs-2 fw-bold text-dark">
              {summaryLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                `${totalOrders}`
              )}
            </div>
          </div>
        </Col>

        <Col md={4}>
          <div
            className="p-4 rounded-4 h-100"
            style={{ backgroundColor: '#EFF6FF' }}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div
                className="text-secondary text-uppercase fw-semibold"
                style={{ fontSize: '11px', letterSpacing: '0.5px' }}
              >
                AVG. ORDER VALUE
              </div>
            </div>
            <div className="fs-2 fw-bold text-dark">
              {revenueLoading || summaryLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                `Rs. ${averageOrderValue}`
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Daily Revenue Line Chart */}
      <div className="position-relative w-100 pt-2">
        {summaryLoading ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: 160 }}
          >
            <Spinner animation="border" variant="primary" size="sm" />
          </div>
        ) : points.length > 0 ? (
          <div className="position-relative w-100">
            {/* Tooltip on Hover */}
            {activeHoveredPoint && (
              <div
                className="position-absolute shadow-sm rounded-3 px-3 py-2 bg-dark text-white text-center pointer-events-none"
                style={{
                  left: `${(activeHoveredPoint.x / svgWidth) * 100}%`,
                  top: -10,
                  transform: 'translate(-50%, -100%)',
                  zIndex: 10,
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                }}
              >
                <div className="fw-semibold">
                  Rs. {Number(activeHoveredPoint.amount).toLocaleString()}
                </div>
                <div className="text-white-50 small">
                  {moment(activeHoveredPoint.date).format('MMM D, YYYY')} •{' '}
                  {activeHoveredPoint.no_of_orders} order
                  {activeHoveredPoint.no_of_orders === 1 ? '' : 's'}
                </div>
              </div>
            )}

            <svg
              viewBox={`0 0 ${svgWidth} ${svgHeight}`}
              className="w-100 d-block overflow-visible"
              style={{ maxHeight: 180 }}
            >
              <defs>
                <linearGradient
                  id="revenueAreaGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#085AFC" stopOpacity="0.25" />
                  <stop offset="65%" stopColor="#085AFC" stopOpacity="0.06" />
                  <stop offset="100%" stopColor="#085AFC" stopOpacity="0.0" />
                </linearGradient>
              </defs>

              {/* Dotted horizontal grid lines */}
              <line
                x1={padX}
                y1={padTop + chartHeight * 0.35}
                x2={padX + chartWidth}
                y2={padTop + chartHeight * 0.35}
                stroke="#E2E8F0"
                strokeDasharray="4 4"
                strokeWidth="1"
              />
              <line
                x1={padX}
                y1={padTop + chartHeight * 0.75}
                x2={padX + chartWidth}
                y2={padTop + chartHeight * 0.75}
                stroke="#E2E8F0"
                strokeDasharray="4 4"
                strokeWidth="1"
              />

              {/* Area under the curve */}
              {areaD && <path d={areaD} fill="url(#revenueAreaGradient)" />}

              {/* Blue smooth line */}
              {pathD && (
                <path
                  d={pathD}
                  fill="none"
                  stroke="#085AFC"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {/* Data points (circles) */}
              {points.map((p, idx) => {
                const isHovered = hoveredIndex === idx;
                const isToday = p.isToday;

                return (
                  <g key={idx}>
                    {/* Visual point */}
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={isHovered ? 6 : isToday ? 5 : 3.5}
                      fill="#085AFC"
                      stroke="#FFFFFF"
                      strokeWidth={isHovered ? 2.5 : isToday ? 2.5 : 1.5}
                      style={{
                        transition: 'r 0.15s ease, stroke-width 0.15s ease',
                      }}
                    />

                    {/* Transparent touch/hover target */}
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={18}
                      fill="transparent"
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => setHoveredIndex(idx)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    />
                  </g>
                );
              })}

              {/* Bottom X-axis labels */}
              {points.map((p, idx) => {
                // If many points (e.g. 30 days), space out labels to prevent overlapping
                if (
                  points.length > 10 &&
                  idx % Math.ceil(points.length / 7) !== 0 &&
                  idx !== points.length - 1
                ) {
                  return null;
                }

                const isToday = p.isToday;

                if (isToday) {
                  return (
                    <g
                      key={`label-${idx}`}
                      transform={`translate(${p.x}, ${svgHeight - 10})`}
                    >
                      <circle cx="-22" cy="-4" r="3" fill="#085AFC" />
                      <text
                        x="0"
                        y="0"
                        textAnchor="middle"
                        fill="#085AFC"
                        fontSize="12.5"
                        fontWeight="600"
                        fontFamily="var(--primary-font, sans-serif)"
                      >
                        Today
                      </text>
                    </g>
                  );
                }

                return (
                  <text
                    key={`label-${idx}`}
                    x={p.x}
                    y={svgHeight - 10}
                    textAnchor="middle"
                    fill="#6B7280"
                    fontSize="12.5"
                    fontWeight="500"
                    fontFamily="var(--primary-font, sans-serif)"
                  >
                    {points.length > 10
                      ? moment(p.date).format('MMM D')
                      : p.dayLabel}
                  </text>
                );
              })}
            </svg>
          </div>
        ) : (
          <div
            className="d-flex align-items-center justify-content-center text-secondary small py-4"
            style={{ height: 120 }}
          >
            No revenue recorded for this period
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(SalesPerformance);
